import { useState } from "react";
import { Reveal } from "../components/Reveal";
import { PageHero, usePageTitle, FlagDot } from "../components/Shared";
import { TOGO_ABROAD, FOREIGN_IN_TOGO } from "../data/ambassades";
import { Search, MapPin, Phone, AlertCircle, Building } from "lucide-react";
import { useLang } from "../i18n";

const COPY = {
  fr: {
    meta: "Ambassades & consulats",
    eyebrow: "Répertoire diplomatique",
    h1a: "Ambassades",
    h1b: "& consulats.",
    sub: "Les coordonnées des représentations du Togo à l'étranger et des ambassades étrangères à Lomé — pour vos légalisations, visas, passeports et démarches consulaires.",
    disclaimer: <><strong>Informations indicatives.</strong> Les coordonnées des ambassades et consulats peuvent évoluer : vérifiez-les avant tout déplacement, notamment sur le site du Ministère togolais des Affaires étrangères. DOC TOGO peut effectuer ces vérifications et démarches consulaires pour vous.</>,
    tabTogo: "Représentations du Togo à l'étranger",
    tabEtranger: "Ambassades étrangères à Lomé",
    search: "Rechercher un pays, une ville…",
    empty: (q) => `Aucun résultat pour « ${q} ».`,
  },
  en: {
    meta: "Embassies & consulates",
    eyebrow: "Diplomatic directory",
    h1a: "Embassies",
    h1b: "& consulates.",
    sub: "Contact details for Togo's representations abroad and foreign embassies in Lomé — for your legalisations, visas, passports and consular procedures.",
    disclaimer: <><strong>Indicative information.</strong> Embassy and consulate details may change: verify them before travelling, in particular on the Togolese Ministry of Foreign Affairs website. DOC TOGO can carry out these checks and consular procedures for you.</>,
    tabTogo: "Togo's representations abroad",
    tabEtranger: "Foreign embassies in Lomé",
    search: "Search a country, a city…",
    empty: (q) => `No result for "${q}".`,
  },
};

const EmbassyCard = ({ item, index, lang }) => (
  <div
    data-testid={`embassy-card-${index}`}
    className="flex h-full flex-col rounded-2xl border border-brand/10 bg-white p-6 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-lg"
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gold">{lang === "en" ? item.type_en || item.type : item.type}</div>
        <h3 className="mt-1.5 font-display text-lg font-bold text-slate-900">{lang === "en" ? item.pays_en || item.pays : item.pays}</h3>
      </div>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-light text-brand">
        <Building className="h-5 w-5" />
      </span>
    </div>
    <div className="mt-4 flex-1 space-y-2 text-sm text-slate-600">
      <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand/50" /> {lang === "en" ? item.adresse_en || item.adresse : item.adresse}</p>
      {item.tel && <p className="flex items-center gap-2"><Phone className="h-4 w-4 shrink-0 text-brand/50" /> {item.tel}</p>}
    </div>
  </div>
);

export default function Ambassades() {
  const { lang } = useLang();
  const c = COPY[lang];
  usePageTitle(c.meta);
  const [tab, setTab] = useState("togo");
  const [q, setQ] = useState("");

  const list = (tab === "togo" ? TOGO_ABROAD : FOREIGN_IN_TOGO).filter((e) =>
    `${e.pays} ${e.pays_en || ""} ${e.ville}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="ambassades-page">
      <PageHero eyebrow={c.eyebrow} lines={[<>{c.h1a}</>, <>{c.h1b}</>]} sub={c.sub} />
      <section className="bg-ivory py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="mb-8 flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold-glow/50 p-5">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <p className="text-sm leading-relaxed text-slate-700">{c.disclaimer}</p>
            </div>
          </Reveal>
          <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex rounded-full border border-brand/15 bg-white p-1" data-testid="ambassades-tabs">
              <button
                onClick={() => setTab("togo")}
                data-testid="ambassades-tab-togo"
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === "togo" ? "bg-brand text-white" : "text-slate-500 hover:text-brand"}`}
              >
                <FlagDot /> {c.tabTogo}
              </button>
              <button
                onClick={() => setTab("etranger")}
                data-testid="ambassades-tab-etranger"
                className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === "etranger" ? "bg-brand text-white" : "text-slate-500 hover:text-brand"}`}
              >
                {c.tabEtranger}
              </button>
            </div>
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="ambassades-search"
                placeholder={c.search}
                className="w-full rounded-full border border-brand/15 bg-white py-3 pl-11 pr-4 text-sm outline-none transition-[border-color,box-shadow] duration-300 focus:border-brand focus:ring-2 focus:ring-brand/15"
              />
            </div>
          </div>
          {list.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">{c.empty(q)}</p>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="ambassades-list">
              {list.map((item, i) => (
                <Reveal key={`${item.pays}-${item.ville}`} delay={Math.min(i * 0.04, 0.3)}>
                  <EmbassyCard item={item} index={i} lang={lang} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
